import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import bowser from 'bowser';
import { translate } from '../../services/L10n';
import { t } from '../../translations';
import _ from 'lodash';
import { selectUseStaticToolTip } from '../../state/userAgent/userAgentSelectors';
import useHover from '../../hooks/useHover';
import {
  selectUseMetric,
  selectWriteDirection,
} from '../../state/dexfSettings/dexfSettingsSelectors';
import styles from './Slider.module.less';
import { getMeasurementBySoM } from '../../services/products/productHandler';

const Slider = ({
  direction,
  useStaticToolTip,
  onRelease,
  disabled,
  useMetricMeasures,
  minVal,
  maxVal,
  step,
  initialValue,
}) => {
  const [value, setValue] = useState(initialValue);
  const [input, showTooltip] = useHover(useStaticToolTip);
  const unit = useMetricMeasures
    ? translate(t.MEASURE_VALUE_CM)
    : translate(t.MEASURE_VALUE_IN);
  const valueMax = maxVal - (maxVal % step);
  const valueMin = minVal + (step - (minVal % step));
  const getDisplayValues = {
    min: getMeasurementBySoM(useMetricMeasures, minVal),
    max: getMeasurementBySoM(useMetricMeasures, maxVal),
    current: `${getMeasurementBySoM(useMetricMeasures, value)} ${unit}`,
  };

  const _onRelease = () => onRelease(value);

  const inputRange = _.range(valueMin, valueMax, step);
  inputRange.push(valueMax);

  if (maxVal !== inputRange[inputRange.length - 1]) {
    inputRange.push(maxVal);
  }
  if (minVal !== inputRange[0]) {
    inputRange.unshift(minVal);
  }

  /**
   * Handles value change
   * @param value
   */
  const onChange = ({ target: { value } }) => setValue(inputRange[value]);

  const sliderMax = inputRange.length - 1;

  let inputValue;
  if (!inputRange.includes(value)) {
    let closestValue, minDelta;
    inputRange.forEach(rangeValue => {
      const delta = Math.abs(value - rangeValue);
      if (!minDelta || delta < minDelta) {
        minDelta = delta;
        closestValue = rangeValue;
      }
    });

    inputValue = inputRange.indexOf(closestValue);
  } else {
    inputValue = inputRange.indexOf(value);
  }

  const getSliderStyle = (sliderMax, inputValue) => {
    if (bowser.msie || bowser.msedge) return {};
    if (disabled) {
      return {
        backgroundColor: '#ccc',
        height: '2px',
      };
    }
    const val = inputValue / sliderMax;

    return {
      backgroundColor: '#ccc',
      backgroundImage: `linear-gradient(180deg, white 0 33%, transparent 33% 66%, white 66% 100%),
        linear-gradient(to ${direction}, #407ab1 0 ${val * 100}%, #ccc ${
        val * 100
      }% 100%)`,
      height: '6px',
    };
  };

  const getTooltipPosition = (sliderMax, inputValue) => {
    const tooltipPos = (inputValue / sliderMax) * 100;

    if (direction === 'right') {
      return {
        right: `${tooltipPos}%`,
      };
    } else {
      return {
        left: `${tooltipPos}%`,
      };
    }
  };

  /**
   * Renders tooltip element
   *
   * @returns {JSX.Element}
   */
  const renderTooltip = () =>
    showTooltip && (
      <div className={styles.outputContainer}>
        <output
          htmlFor="clr-width"
          className={styles.rangeOutput}
          style={getTooltipPosition(sliderMax, inputValue)}
        >
          {getDisplayValues.current}
          <div className={styles.arrow} />
        </output>
      </div>
    );

  /**
   * Renders slider element
   *
   * @returns {JSX.Element}
   */
  const renderSlider = () => (
    <div ref={input} className={styles.inputContainer}>
      <input
        type="range"
        className={styles.sliderInput}
        style={getSliderStyle(sliderMax, inputValue)}
        disabled={disabled}
        min="0"
        max={sliderMax}
        value={inputValue}
        step="1"
        onChange={onChange}
        onMouseUp={_onRelease}
        onTouchEnd={_onRelease}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {!disabled && <span>{getDisplayValues.min}</span>}
      <div className={styles.rangeContainer}>
        {renderTooltip()}
        {renderSlider()}
      </div>
      {!disabled && <span>{getDisplayValues.max}</span>}
    </div>
  );
};

Slider.propTypes = {
  disabled: PropTypes.bool.isRequired,
  minVal: PropTypes.number.isRequired,
  maxVal: PropTypes.number.isRequired,
  initialValue: PropTypes.number.isRequired,
  step: PropTypes.number,
  onRelease: PropTypes.func.isRequired,
  useStaticToolTip: PropTypes.bool.isRequired,
  useMetricMeasures: PropTypes.bool.isRequired,
  direction: PropTypes.string.isRequired,
};

export default connect(state => ({
  useStaticToolTip: selectUseStaticToolTip(state),
  useMetricMeasures: selectUseMetric(state),
  direction: selectWriteDirection(state),
}))(Slider);
